import { HyruleToken } from '@nintendo/hyrule-react-commons';
import { useQuery } from 'react-query';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import {
    DigitalOrderStatusMap,
    PhysicalOrderStatusMap,
    OrderLineStatus,
    PartOrderApi,
    ProductType,
} from '../../../api';
import { OrderModel, OrderSubType, OrderType } from '../orders/types';
import {
    PartOrderApiModel,
    PartOrderApiModelRequest,
    PartOrderEntryApiModel,
} from '../../../api/models';
import { OrdersHookData } from './types';
import Utils from '../../../shared/Utils';

export const usePartOrders = (
    naid: string,
    email: string,
    token: HyruleToken,
    correlationId: string,
    orderNumber?: string,
): OrdersHookData => {
    const { data, isLoading, isRefetching, isError, refetch } = useQuery<PartOrderApiModel | null>(
        ['part-orders', { naid, email, orderNumber }],
        async () => {
            if ((naid || email || orderNumber) && correlationId && token) {
                const requests: PartOrderApiModelRequest[] = [];
                if (email) {
                    requests.push({ email });
                }
                if (naid) {
                    requests.push({ naid });
                }
                if (orderNumber && !naid && !email) {
                    requests.push({ orderNumber });
                }

                const allItems = (
                    await Promise.all(
                        requests.map((req) =>
                            PartOrderApi.getPartOrders(req, token, correlationId),
                        ),
                    )
                )
                    .map((response) => response?.items || [])
                    .flat();

                return {
                    items: Array.from(
                        new Map(allItems.map((item) => [item.order_number, item])).values(),
                    ),
                };
            }

            return Promise.resolve(null);
        },
        {
            cacheTime: 1000 * 60 * 10, // TODO what is our TTL?
        },
    );

    const getOrderSubType = (partOrder: PartOrderEntryApiModel) => {
        const partOrderNumber = partOrder.order_number.toString();
        if (partOrderNumber.length === 10) {
            return OrderSubType.ServiceOrderStoreMagento;
        }
        if (partOrderNumber.length === 11) {
            return OrderSubType.MyNintendoStorePhysical;
        }

        return OrderSubType.ESHOP;
    };

    const orders: OrderModel[] =
        data?.items?.map((partOrder: PartOrderEntryApiModel) => ({
            type: OrderType.PHYSICAL,
            subType: getOrderSubType(partOrder),
            orderSource: partOrder.order_source,
            channel: partOrder.source_channel_type_name,
            id: partOrder.order_number,
            date: partOrder.creation_date ? dayjs(partOrder.creation_date) : undefined,
            country: partOrder.country,
            totalAmount: partOrder.order_total,
            refundAmount: partOrder.return_amount,
            refundDate: partOrder.return_date,
            isGuestOrder: partOrder.guest_order === 'true' || !Utils.isValidNAID(partOrder.naid),
            items:
                partOrder.items?.map((item) => ({
                    description: item.description,
                    orderStatus: parseStatus(
                        item.order_status as OrderLineStatus,
                        item.product_type,
                    ),
                    shipDate: dayjs(item.ship_date),
                    carrierName: item.carrier_name,
                    trackingNumber: item.tracking_number,
                    trackingUrl: item.track_hyperlink,
                })) || [],
        })) || [];

    // TODO DICE-1542 debug why this is needed and refactor
    useEffect(() => {
        refetch();
    }, [naid, email, orderNumber]);

    return {
        orders,
        isLoading: isLoading || isRefetching,
        isNotFound: !isError && orders.length === 0,
        isError,
        refetch,
    };
};

const parseStatus = (status: OrderLineStatus, productType: string): string => {
    let map: Map<OrderLineStatus, string> = new Map();
    if (productType === ProductType.Digital) {
        map = DigitalOrderStatusMap;
    } else if (productType === ProductType.Physical) {
        map = PhysicalOrderStatusMap;
    } else {
        return status;
    }

    return map.get(status) || 'Unavailable';
};
